import React, { useState, useCallback, useEffect } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import "../custom.css";
import ButtonWrapper from "./ButtonWrapper";
import { useAuth } from "../auth/authContext";
import { 
  useProfile, 
  useUpdateProfile, 
  usePaymentMethods,
  useSetDefaultPaymentMethod,
  useRemovePaymentMethod,
  useDeleteAccount,
  useCreateCustomerPortal
} from "../api/hooks";
import { ProfileResponse } from "../api/ApiClient";
import AddCardModal from "./AddCardModal";
import DeleteAccountModal from "./DeleteAccountModal";

type TabType = "personal" | "payment" | "preferences";

const Details = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { data: profile } = useProfile();
  
  // Initialize tab from URL route parameter or default to "personal"
  const getInitialTab = (): TabType => {
    if (id === "payment" || id === "preferences") {
      return id;
    }
    return "personal";
  };
  
  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab());
  
  // Update URL when tab changes
  useEffect(() => {
    const newPath = activeTab === "personal" ? "/details" : `/details/${activeTab}`;
    navigate(newPath, { replace: true });
  }, [activeTab, navigate]);
  
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  return (
    <Container className="px-5">
      <Row className="mt-5">
        <Col>
          <h3 className="mb-1">Personal Profile</h3>
          <p className="text-muted small mb-0">Manage your account details and preferences</p>
        </Col>
      </Row>
      
      {profile?.validationErrors &&
        Object.keys(profile.validationErrors).length > 0 && (
          <Row className="mt-3">
            <Col>
              <div className="alert alert-danger">
                <strong>Please fix the following errors:</strong>
                <ul className="mb-0 mt-2">
                  {Object.entries(profile.validationErrors).map(
                    ([key, value], index) => {
                      return (
                        <li key={index}>
                          {value}
                        </li>
                      );
                    }
                  )}
                </ul>
              </div>
            </Col>
          </Row>
        )}
      
      <Row className="mt-4">
        <Col>
          <div className="d-grid gap-2" style={{ gridTemplateColumns: "repeat(3, 1fr)", backgroundColor: "#eee", padding: "5px", borderRadius: "5px" }}>
            <button
              className={`btn ${
                activeTab === "personal" ? "btn-light" : "btn-secondary"
              }`}
              onClick={() => handleTabChange("personal")}
              style={{
                backgroundColor: activeTab === "personal" ? "#ffffff" : "#eee",
                border: "1px solid #eee",
                color: "#495057",
              }}
            >
              Personal Info
            </button>
            <button
              className={`btn ${
                activeTab === "payment" ? "btn-light" : "btn-secondary"
              }`}
              onClick={() => handleTabChange("payment")}
              style={{
                backgroundColor: activeTab === "payment" ? "#ffffff" : "#eee",
                border: "1px solid #eee",
                color: "#495057",
              }}
            >
              Payment Methods
            </button>
            <button
              className={`btn ${
                activeTab === "preferences" ? "btn-light" : "btn-secondary"
              }`}
              onClick={() => handleTabChange("preferences")}
              style={{
                backgroundColor: activeTab === "preferences" ? "#ffffff" : "#eee",
                border: "1px solid #eee",
                color: "#495057",
              }}
            >
              Preferences
            </button>
          </div>
        </Col>
      </Row>
      <Row className="mt-4 mb-4">
        <Col>
          {activeTab === "personal" && <PersonalDetails />}
          {activeTab === "payment" && <PaymentDetails />}
          {activeTab === "preferences" && <ParkingPreferences />}
        </Col>
      </Row>
    </Container>
  );
};

const PaymentDetails = () => {
  const { token } = useAuth();
  const { data: profile, isLoading, error } = useProfile();
  const { data: paymentMethodsData, isLoading: paymentMethodsLoading } = usePaymentMethods();
  const setDefaultMutation = useSetDefaultPaymentMethod();
  const removeMutation = useRemovePaymentMethod();
  const createPortalMutation = useCreateCustomerPortal();
  const [showAddCardModal, setShowAddCardModal] = useState(false);

  const handleSetDefault = (paymentMethodId: string) => {
    if (window.confirm("Set this as your default payment method?")) {
      setDefaultMutation.mutate(paymentMethodId);
    }
  };

  const handleRemove = (paymentMethodId: string) => {
    if (window.confirm("Are you sure you want to remove this payment method?")) {
      removeMutation.mutate(paymentMethodId);
    }
  };

  const handleOpenCustomerPortal = async () => {
    try {
      const response = await createPortalMutation.mutateAsync(window.location.origin + "/details/payment");
      if (response.data?.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      console.error("Error opening customer portal:", error);
    }
  };

  if (isLoading || paymentMethodsLoading) {
    return <div>Loading...</div>;
  }
  if (error) {
    return <div>Error loading payment details</div>;
  }

  if (!profile) {
    return <div>No profile data available</div>;
  }

  // Use the new API response for payment methods
  const paymentMethods = paymentMethodsData?.paymentMethods || [];
  const defaultPaymentMethodId = paymentMethodsData?.defaultPaymentMethodId;

  return (
    <>
      <AddCardModal show={showAddCardModal} onHide={() => setShowAddCardModal(false)} profile={profile} />
      <div
        style={{
          backgroundColor: "#eee",
          borderRadius: "0.5rem",
          padding: "1.5rem",
        }}
      >
        <Row className="mb-4">
          <Col>
            <h5 className="mb-1">Payment Methods</h5>
            <p className="text-muted small mb-0">Manage your payment cards and billing information</p>
          </Col>
          <Col xs="auto" className="ms-auto">
            <div className="d-flex gap-2">
              <button 
                onClick={() => setShowAddCardModal(true)}
                className="btn btn-sm btn-outline-secondary"
                style={{ whiteSpace: "nowrap" }}
              >
                Add Card
              </button>
              {paymentMethods?.length > 0 && (
                <button 
                  onClick={handleOpenCustomerPortal}
                  className="btn btn-sm"
                  style={{ 
                    whiteSpace: "nowrap",
                    backgroundColor: "#fe9015",
                    borderColor: "#fe9015",
                    color: "white"
                  }}
                  disabled={createPortalMutation.isPending}
                >
                  {createPortalMutation.isPending ? "Opening..." : "Manage in Stripe"}
                </button>
              )}
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            {paymentMethods?.length === 0 ? (
              <div className="text-muted">No payment methods added yet</div>
            ) : (
              <ul className="list-group mb-3">
                {paymentMethods.map((pm: any) => {
                  const isDefault = pm.id === defaultPaymentMethodId;
                  return (
                    <li className="list-group-item d-flex align-items-center" key={pm.id} style={{ backgroundColor: isDefault ? "#fff4e5" : "transparent" }}>
                      <div className="flex-grow-1">
                        <span className="fw-semibold">{pm.card?.brand || "Unknown"}</span>
                        <span className="ms-3">•••• {pm.card?.last4 || "N/A"}</span>
                        <span className="ms-3 text-muted">
                          Expires {pm.card?.expMonth ? String(pm.card.expMonth).padStart(2, '0') : "N/A"}/{pm.card?.expYear || "N/A"}
                        </span>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        {isDefault ? (
                          <span className="badge" style={{ backgroundColor: "#fe9015", fontSize: "0.875rem", padding: "0.5rem 0.75rem" }}>
                            Default
                          </span>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleSetDefault(pm.id)}
                            disabled={setDefaultMutation.isPending}
                            style={{ whiteSpace: "nowrap" }}
                          >
                            {setDefaultMutation.isPending ? "Setting..." : "Set Default"}
                          </button>
                        )}
                        <button
                          className="btn btn-sm text-danger"
                          onClick={() => handleRemove(pm.id)}
                          disabled={removeMutation.isPending}
                          style={{ whiteSpace: "nowrap", border: "none" }}
                        >
                          {removeMutation.isPending ? "Removing..." : "Remove"}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Col>
        </Row>
      </div>
    </>
  );
};

const PersonalDetails = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { data: profile, isLoading, error } = useProfile();
  const updateProfileMutation = useUpdateProfile();
  const deleteAccountMutation = useDeleteAccount();

  const [editMode, setEditMode] = useState(false);
  const [parkerDetails, setParker] = useState<ProfileResponse | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  var dummyStreetTypes = [
    "",
    "Alley",
    "Arcade",
    "Avenue",
    "Boulevard",
    "Bypass",
    "Circuit",
    "Close",
    "Corner",
    "Court",
    "Crescent",
    "Cul-de-sac",
    "Drive",
    "Esplanade",
    "Green",
    "Grove",
    "Highway",
    "Junction",
    "Lane",
    "Link",
    "Mews",
    "Parade",
    "Place",
    "Ridge",
    "Road",
    "Square",
    "Street",
    "Terrace",
  ];

  var states = ["", "ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];

  const handleChange = useCallback(
    // structure map to support React.ChangeEvent<HTMLInputElement> & React.ChangeEvent<HTMLSelectElement>
    (e: { target: { name: string; value: string } }) => {
      if (!parkerDetails) return;
      setParker({
        ...parkerDetails,
        [e.target.name]: e.target.value,
      });
    },
    [parkerDetails]
  );

  const handleTermsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!parkerDetails) return;
      setParker({
        ...parkerDetails,
        agreeTermsConditions: e.target.checked,
      });
    },
    [parkerDetails]
  );

  const handleChangePostcode = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (!parkerDetails) return;
      if (
        value.toString().match(/^[0-9]{1,4}$/) != null ||
        value.toString() === ""
      ) {
        setParker({
          ...parkerDetails,
          postcode: e.target.value,
        });
      }
    },
    [parkerDetails]
  );

  const onSaveDetails = () => {
    if (!parkerDetails) return;
    updateProfileMutation.mutate({
      firstName: parkerDetails.firstName || undefined,
      lastName: parkerDetails.lastName || undefined,
      mobile: parkerDetails.mobile || undefined,
      streetName: parkerDetails.streetName || undefined,
      streetNumber: parkerDetails.streetNumber || undefined,
      streetType: parkerDetails.streetType || undefined,
      suburb: parkerDetails.suburb || undefined,
      state: parkerDetails.state || undefined,
      postcode: parkerDetails.postcode || undefined,
      agreeTermsConditions: parkerDetails.agreeTermsConditions || undefined,
    });
    setEditMode(false);
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccountMutation.mutateAsync();
      // Log out and redirect to home
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
    } finally {
      setShowDeleteModal(false);
    }
  };

  React.useEffect(() => {
    if (profile) {
      setParker(profile);
    }
  }, [profile]);

  if (isLoading) {
    return <div>Loading profile...</div>;
  }

  if (error) {
    return <div>Error loading profile: {error.message}</div>;
  }

  if (!profile || !parkerDetails) {
    return <div>No profile data available</div>;
  }

  return (
    <>
      <div
        style={{
          backgroundColor: "#eee",
          borderRadius: "0.5rem",
          padding: "1.5rem",
        }}
      >
        <Row className="mb-4">
          <Col>
            <h5 className="mb-1">Personal Information</h5>
            <p className="text-muted small mb-0">Your account details and contact information</p>
          </Col>
          <Col xs="auto" className="ms-auto">
            {!editMode ? (
              <button
                className="btn btn-outline-secondary"
                onClick={() => setEditMode(true)}
              >
                Edit
              </button>
            ) : (
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setEditMode(false)}
                >
                  Cancel
                </button>
                <ButtonWrapper
                  buttonText="Save"
                  fullWidth={false}
                  onClick={() => onSaveDetails()}
                />
              </div>
            )}
          </Col>
        </Row>

        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="personalDetailsFirstName">
                <Form.Label className={"text-muted small"}>First Name *</Form.Label>
                <Form.Control
                  disabled={!editMode}
                  name="firstName"
                  value={parkerDetails.firstName ?? ""}
                  onChange={handleChange}
                  className=""
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="personalDetailsLastName">
                <Form.Label className={"text-muted small"}>Last Name *</Form.Label>
                <Form.Control
                  disabled={!editMode}
                  name="lastName"
                  value={parkerDetails.lastName ?? ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-0">
            <Col md={6}>
              <Form.Group controlId="personalDetailsMobile">
                <Form.Label className={"text-muted small"}>Mobile *</Form.Label>
                <Form.Control
                  disabled={!editMode}
                  name="mobile"
                  value={parkerDetails.mobile ?? ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="personalDetailsEmail">
                <Form.Label className={"text-muted small"}>Email *</Form.Label>
                <Form.Control
                  disabled={true}
                  type="email"
                  name="email"
                  defaultValue={parkerDetails.email ?? ""}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-0">
            <Col md={6}>
              <Form.Group controlId="personalDetailsStreetNumber">
                <Form.Label className={"text-muted small"}>Street Number</Form.Label>
                <Form.Control
                  disabled={!editMode}
                  name="streetNumber"
                  value={parkerDetails.streetNumber ?? ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="personalDetailsStreetName">
                <Form.Label className={"text-muted small"}>Street Name</Form.Label>
                <Form.Control
                  disabled={!editMode}
                  name="streetName"
                  value={parkerDetails.streetName ?? ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-0">
            <Col md={6}>
              <Form.Group controlId="personalDetailsStreetType">
                <Form.Label className={"text-muted small"}>Street Type</Form.Label>
                <Form.Select
                  disabled={!editMode}
                  name="streetType"
                  value={parkerDetails.streetType ?? ""}
                  onChange={handleChange}
                >
                  {dummyStreetTypes.map((type, i) => (
                    <option key={i} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="personalDetailsStreetSuburb">
                <Form.Label className={"text-muted small"}>Suburb</Form.Label>
                <Form.Control
                  disabled={!editMode}
                  name="suburb"
                  value={parkerDetails.suburb ?? ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-0">
            <Col md={6}>
              <Form.Group controlId="personalDetailsState">
                <Form.Label className={"text-muted small"}>State</Form.Label>
                <Form.Select
                  disabled={!editMode}
                  name="state"
                  value={parkerDetails.state ?? ""}
                  onChange={handleChange}
                >
                  {states.map((state, i) => (
                    <option key={i} value={state}>
                      {state}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="personalDetailsPostcode">
                <Form.Label className={"text-muted small"}>Postcode</Form.Label>
                <Form.Control
                  disabled={!editMode}
                  name="postcode"
                  value={parkerDetails.postcode ?? ""}
                  onChange={handleChangePostcode}
                  placeholder="----"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-0">
            <Col md={6}>
              <Form.Group controlId="personalDetailsTerms">
                <Form.Label className={"text-muted small"}>Terms & Conditions</Form.Label>
                <div className="mt-2">
                  <Form.Check
                    disabled={!editMode || !!profile.agreeTermsConditions}
                    type="checkbox"
                    id="terms"
                    name="terms"
                    checked={parkerDetails.agreeTermsConditions || false}
                    onChange={handleTermsChange}
                    label="I agree to the Terms and Conditions"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </div>

      {/* Delete Account Section */}
      <div
        className="mt-4"
        style={{
          backgroundColor: "#fff5f5",
          borderRadius: "0.5rem",
          padding: "1.5rem",
          border: "1px solid #dc3545",
        }}
      >
        <Row className="align-items-center g-0">
          <Col>
            <h5 className="text-danger mb-1">
              Danger Zone
            </h5>
            <p className="text-muted small mb-0">
              Delete account and all associated data
            </p>
          </Col>
          <Col xs="auto" className="ms-auto">
            <button
              className="btn btn-danger"
              onClick={() => setShowDeleteModal(true)}
              disabled={editMode}
            >
              <i className="bi bi-trash-fill me-2"></i>
              Delete My Account
            </button>
          </Col>
        </Row>
      </div>

      <DeleteAccountModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        isDeleting={deleteAccountMutation.isPending}
      />
    </>
  );
};

const ParkingPreferences = () => {
  const { data: profile, isLoading, error } = useProfile();
  const updateProfileMutation = useUpdateProfile();

  const [editMode, setEditMode] = useState(false);
  const [parkerDetails, setParker] = useState<ProfileResponse | null>(null);

  const handleChange = useCallback(
    (e: { target: { name: string; value: string } }) => {
      if (!parkerDetails) return;
      setParker({
        ...parkerDetails,
        [e.target.name]: e.target.value,
      });
    },
    [parkerDetails]
  );

  const handleNotificationChange = useCallback(
    (field: "notifyByEmail" | "notifyBySms") => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!parkerDetails) return;
      setParker({
        ...parkerDetails,
        [field]: e.target.checked,
      });
    },
    [parkerDetails]
  );

  const handleSpecificNotificationChange = useCallback(
    (field: "notifyReservationReminders" | "notifyPaymentReceipts" | "notifyMarketingEmails") => 
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!parkerDetails) return;
      setParker({
        ...parkerDetails,
        [field]: e.target.checked,
      });
    },
    [parkerDetails]
  );

  const onSaveDetails = () => {
    if (!parkerDetails) return;
    updateProfileMutation.mutate({
      bayPreference: parkerDetails.bayPreference || undefined,
      permitNumber: parkerDetails.permitNumber || undefined,
      notifyByEmail: parkerDetails.notifyByEmail,
      notifyBySms: parkerDetails.notifyBySms,
      notifyReservationReminders: parkerDetails.notifyReservationReminders,
      notifyPaymentReceipts: parkerDetails.notifyPaymentReceipts,
      notifyMarketingEmails: parkerDetails.notifyMarketingEmails,
    });
    setEditMode(false);
  };

  React.useEffect(() => {
    if (profile) {
      setParker(profile);
    }
  }, [profile]);

  if (isLoading) {
    return <div>Loading preferences...</div>;
  }

  if (error) {
    return <div>Error loading preferences: {error.message}</div>;
  }

  if (!profile || !parkerDetails) {
    return <div>No profile data available</div>;
  }

  return (
    <>
      <div
        style={{
          backgroundColor: "#eee",
          borderRadius: "0.5rem",
          padding: "1.5rem",
        }}
      >
        <Row className="mb-4">
          <Col>
            <h5 className="mb-1">Preferences</h5>
            <p className="text-muted small mb-0">Customise your notification and parking preferences</p>
          </Col>
          <Col xs="auto" className="ms-auto">
            {!editMode ? (
              <button
                className="btn btn-outline-secondary"
                onClick={() => setEditMode(true)}
              >
                Edit
              </button>
            ) : (
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setEditMode(false)}
                >
                  Cancel
                </button>
                <ButtonWrapper
                  buttonText="Save"
                  fullWidth={false}
                  onClick={() => onSaveDetails()}
                />
              </div>
            )}
          </Col>
        </Row>

        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="notificationPreferences">
                <Form.Label className={"text-muted small"}>Notification Methods</Form.Label>
                <div className="mt-2">
                  <Form.Check
                    disabled={!editMode}
                    type="checkbox"
                    id="notifyEmail"
                    name="notifyEmail"
                    checked={parkerDetails.notifyByEmail || false}
                    onChange={handleNotificationChange("notifyByEmail")}
                    label="Email notifications"
                    className="mb-2"
                  />
                  <Form.Check
                    disabled={!editMode}
                    type="checkbox"
                    id="notifySms"
                    name="notifySms"
                    checked={parkerDetails.notifyBySms || false}
                    onChange={handleNotificationChange("notifyBySms")}
                    label="SMS notifications"
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="specificNotifications">
                <Form.Label className={"text-muted small"}>Specific Notifications</Form.Label>
                <div className="mt-2">
                  <Form.Check
                    disabled={!editMode}
                    type="checkbox"
                    id="notifyReservations"
                    name="notifyReservations"
                    checked={parkerDetails.notifyReservationReminders || false}
                    onChange={handleSpecificNotificationChange("notifyReservationReminders")}
                    label="Reservation reminders"
                    className="mb-2"
                  />
                  <Form.Check
                    disabled={!editMode}
                    type="checkbox"
                    id="notifyPayments"
                    name="notifyPayments"
                    checked={parkerDetails.notifyPaymentReceipts || false}
                    onChange={handleSpecificNotificationChange("notifyPaymentReceipts")}
                    label="Payment receipts"
                    className="mb-2"
                  />
                  <Form.Check
                    disabled={!editMode}
                    type="checkbox"
                    id="notifyMarketing"
                    name="notifyMarketing"
                    checked={parkerDetails.notifyMarketingEmails || false}
                    onChange={handleSpecificNotificationChange("notifyMarketingEmails")}
                    label="Marketing emails"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          
          <Row className="g-3 mt-3">
            <Col md={6}>
              <Form.Group controlId="parkingPreference">
                <Form.Label className={"text-muted small"}>Parking Bay Type</Form.Label>
                <Form.Select
                  aria-label="Bay Preference"
                  disabled={!editMode}
                  value={parkerDetails.bayPreference ?? "Regular"}
                  onChange={handleChange}
                  name="bayPreference"
                >
                  <option value="Regular">Standard Bay</option>
                  <option value="Accessible">Accessible Bay</option>
                  <option value="PWP">Parent with Pram</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="permitNumber">
                <Form.Label className={"text-muted small"}>
                  Permit Number
                  {parkerDetails.bayPreference === "Accessible" ? " *" : ""}
                </Form.Label>
                <Form.Control
                  disabled={!editMode}
                  name="permitNumber"
                  value={parkerDetails.permitNumber ?? ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </div>
    </>
  );
};

export default Details;
